import cx from "classnames";
import { mergeRefs } from "@react-aria/utils";
import { formatDistance, formatDistanceStrict } from "date-fns/esm";

import TextEllipsis from "ds/components/TextEllipsis";
import Tooltip from "ds/components/Tooltip";
import Typography, { TypographyProps } from "ds/components/Typography";
import { getNowTimeMs, unixTimestampToMs } from "utils/time";
import { TooltipReferenceProps } from "ds/components/Tooltip/types";
import { formatDate } from "utils/date";

type TimestampProps = {
  timestamp: number;
  color?: TypographyProps["color"];
  variant?: TypographyProps["variant"];
  strict?: boolean;
  format?: "absolute" | "relative";
};

const Timestamp = ({
  timestamp,
  color,
  variant = "p-body3",
  strict,
  format = "absolute",
}: TimestampProps) => {
  let formattedTimestamp: string | undefined;
  let relativeTime: string | undefined;
  try {
    const timestampMs = unixTimestampToMs(timestamp);
    const now = getNowTimeMs();

    formattedTimestamp = formatDate({
      date: timestampMs,
    });

    relativeTime = strict
      ? formatDistanceStrict(timestampMs, now, { addSuffix: true })
      : formatDistance(timestampMs, now, { addSuffix: true, includeSeconds: true });
  } catch {
    // do nothing
  }

  if (!formattedTimestamp || !relativeTime) {
    return (
      <Typography tag="span" variant={variant} color={color}>
        Invalid date
      </Typography>
    );
  }

  const isRelative = format === "relative";
  const visibleValue = isRelative ? relativeTime : formattedTimestamp;
  const tooltipValue = isRelative ? formattedTimestamp : relativeTime;
  const truncatedTooltipText = `${visibleValue} (${tooltipValue})`;

  return (
    <TextEllipsis tooltip={truncatedTooltipText}>
      {(parentProps, isActive) => (
        <Tooltip
          active={!isActive}
          on={(props: TooltipReferenceProps) => (
            <Typography
              ref={mergeRefs(props.ref, parentProps.ref)}
              className={cx(props.className, parentProps.className)}
              tag="span"
              variant={variant}
              color={color}
            >
              {visibleValue}
            </Typography>
          )}
        >
          {tooltipValue}
        </Tooltip>
      )}
    </TextEllipsis>
  );
};

export default Timestamp;
